// Currency formatting utility: single source of truth
export const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// The words shown in place of a price the contractor has never set. ONE
// constant, because this string appears on seven customer-facing surfaces
// across three portal pages and they must never disagree: a customer seeing
// $0.00 on the dashboard and "Not yet priced" on the invoice for the same
// service is worse than either one alone.
export const PRICE_PENDING_LABEL = 'Not yet priced';

// known === false means the server reported base.source 'missing': no price has
// ever been set for this service, so $0.00 would be a statement the owner never
// made. A legitimate zero, and a comped stop, are KNOWN and still print $0.00.
//
// The test is `known === false` rather than `!known` on purpose. A stale cached
// bundle, or LawnTrack's copy of these pages which does not read the field yet,
// sends undefined. Undefined must fall through to the old behaviour rather than
// label every price on the page as pending.
export const formatPriceOrPending = (amount, known) =>
  (known === false ? PRICE_PENDING_LABEL : formatCurrency(amount));

// Whether a TOTAL can be stated, as opposed to a single line of one. A total is
// only as knowable as its least known part, so an unknown salt price makes the
// total unknown even when the base price is set: adding 0 for the salt would
// print a figure SMALLER than the truth, which is the same understatement as
// printing $0.00 for a missing base, one level down.
//
// Line-level surfaces do not use this. The Base Price row reads priceKnown alone
// and the salt row reads saltPriceKnown alone, because each states its own part.
export const priceIsKnown = (record) =>
  !(record && (record.priceKnown === false || record.saltPriceKnown === false));

// DEPTH PRICING. A stop completed before the storm depth was entered has no
// price yet for a different reason than an unpriced one: it is waiting for the
// storm total, and the customer is told exactly that (design 2.5).
export const PENDING_DEPTH_LABEL = 'Price pending storm total';

// What a portal record's amount reads as: the storm-total wording on a stop
// awaiting depth, "Not yet priced" on an unknown, else the figure.
export const portalPriceText = (record, amount) =>
  (record && record.pendingDepth === true
    ? PENDING_DEPTH_LABEL
    : formatPriceOrPending(amount, priceIsKnown(record)));

// The note beside a balance that leaves some services out.
export const pendingServicesNote = (records) => {
  const list = records || [];
  const depth = list.filter(r => r && r.pendingDepth === true).length;
  const other = list.length - depth;
  const parts = [];
  if (other > 0) parts.push(other === 1 ? '1 service not yet priced' : `${other} services not yet priced`);
  if (depth > 0) parts.push(depth === 1 ? '1 service pending the storm total' : `${depth} services pending the storm total`);
  return parts.join(', ');
};
